import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const RequestFeature = ({ showToast }) => {
  const { user } = useAuth();
  const [featureDescription, setFeatureDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!featureDescription.trim()) {
      showToast('Please describe the feature you want', 'error');
      return;
    }

    setSubmitting(true);
    
    try {
      // Store feature request (simple implementation - stores in backend)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name,
          user_email: user.email,
          role: user.role,
          brand_id: user.brand_id,
          description: featureDescription,
          status: 'pending'
        })
      });

      if (!response.ok) throw new Error('Failed to submit');

      showToast('✅ Feature request submitted successfully!', 'success');
      setFeatureDescription('');
    } catch (error) {
      showToast('Failed to submit feature request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">💡 Request a Feature</h1>
        <p className="text-slate-400">
          Have an idea to improve STREAM? Share your feature requests with us!
        </p>
      </div>

      <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
              Describe Your Feature Idea <span className="text-red-400">*</span>
            </label>
            <textarea
              value={featureDescription}
              onChange={(e) => setFeatureDescription(e.target.value)}
              placeholder="Example: Add ability to schedule campaigns in advance, Support for video creatives, Export analytics as PDF..."
              rows={8}
              required
              className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 resize-none"
              style={{ '--focus-ring-color': '#32B4AC' }}
              onFocus={(e) => e.target.style.borderColor = '#32B4AC'}
              onBlur={(e) => e.target.style.borderColor = ''}
            />
            <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-600 mt-2">
              💡 Be specific about what you want and how it would help your workflow
            </p>
          </div>

          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(50, 180, 172, 0.1)', borderLeft: '3px solid #32B4AC' }}>
            <p className="text-sm text-slate-300 dark:text-slate-300 light:text-slate-700">
              <strong>Submitted by:</strong> {user.name} ({user.email})
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-[0.98]"
            style={{ backgroundColor: '#32B4AC' }}
            onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#2A9A93')}
            onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#32B4AC')}
          >
            {submitting ? 'Submitting...' : '🚀 Submit Feature Request'}
          </button>
        </form>
      </div>

      <div className="mt-6 p-4 bg-slate-800/50 dark:bg-slate-800/50 light:bg-slate-50 rounded-xl border border-slate-700 dark:border-slate-700 light:border-slate-200">
        <h3 className="text-sm font-semibold text-white dark:text-white light:text-slate-900 mb-2">
          ℹ️ What happens next?
        </h3>
        <ul className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600 space-y-1">
          <li>• Your request will be reviewed by the admin team</li>
          <li>• We'll prioritize based on user needs and technical feasibility</li>
          <li>• You'll be notified when your feature is implemented</li>
        </ul>
      </div>
    </div>
  );
};

export default RequestFeature;
